import {
  OktonPageHeader,
  OktonConfirmDialog,
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonSearchFilter,
  OktonSegmentedFilter,
  ColunaGrid,
  useGrid,
  OktonDataGrid,
  TAMANHOS_PAGINA,
  OktonEmptyState,
} from '@okton/platform-ui';
import {
  useToast,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { BookOpen, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useContasBancarias } from '../../services/hooks/useContasBancarias';
import {
  LancamentoExtrato,
  useExtratoBancario,
} from '../../services/hooks/useExtratoBancario';
import { useDeleteLancamentoExtrato } from '../../services/hooks/useExtratoBancarioMutations';
import formatValue from '../../utils/formatValue';
import { formatDataExtrato } from '../../utils/formatDataExtrato';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

const COLUNAS: ColunaGrid<LancamentoExtrato>[] = [
  { chave: 'data_exb', titulo: 'Data', valor: t => t.data_exb, tipo: 'data' },
  { chave: 'descricao_exb', titulo: 'Descrição', valor: t => t.descricao_exb },
  { chave: 'descricao_cts', titulo: 'Conta', valor: t => t.descricao_cts },
  {
    chave: 'referencia_exb',
    titulo: 'Referência',
    valor: t => t.referencia_exb,
  },
  {
    chave: 'fitid_exb',
    titulo: 'FITID',
    valor: t => t.fitid_exb,
  },
  {
    chave: 'valor_exb',
    titulo: 'Valor',
    valor: t => Number(t.valor_exb),
    tipo: 'numero',
    alinhamento: 'right',
  },
  {
    chave: 'conciliado_exb',
    titulo: 'Status',
    valor: t => (t.conciliado_exb === '1' ? 'Conciliado' : 'Pendente'),
    alinhamento: 'center',
  },
];

const ExtratoBancario = () => {
  const history = useHistory();
  // estilos
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  // filtros
  const [filterAccount, setFilterAccount] = useState('');
  const [filtroData, setFiltroData] = useState<FiltroData>(setaFiltroAnual());
  const [filterStatus, setFilterStatus] = useState<
    'all' | 'pending' | 'reconciled'
  >('all');

  // dados — status 'all' porque o filtro de conciliado é aplicado aqui na tela
  const {
    data: accounts = [],
    isLoading: isAccountsLoading,
    isError: isAccountsError,
    refetch: refetchContas,
  } = useContasBancarias();
  const {
    data: transactions = [],
    isLoading: isTxLoading,
    isError: isTxError,
    refetch: refetchExtrato,
  } = useExtratoBancario(
    filterAccount || undefined,
    'all',
    filtroData[1],
    filtroData[2],
  );
  const deleteMutation = useDeleteLancamentoExtrato();

  const toast = useToast();
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const isLoading = isAccountsLoading || isTxLoading;
  const isError = isAccountsError || isTxError;

  const recarregar = () => {
    refetchContas();
    refetchExtrato();
  };

  const remove = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
      refetchExtrato();
    } catch (err) {
      toast({
        title:
          err instanceof Error
            ? err.message
            : 'Erro ao remover o lançamento do extrato.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  const filtered = useMemo(
    () =>
      transactions.filter(tx => {
        if (filterStatus === 'pending' && tx.conciliado_exb === '1')
          return false;
        if (filterStatus === 'reconciled' && tx.conciliado_exb !== '1')
          return false;
        return true;
      }),
    [transactions, filterStatus],
  );

  const [busca, setBusca] = useState('');
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(filtered, COLUNAS, busca);

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="GERAL"
        title="Extrato Bancário"
        subtitle="Lançamentos importados do banco"
        actions={
          <OktonDateFilter
            value={{
              preset: filtroData[0],
              start: filtroData[1],
              end: filtroData[2],
            }}
            onChange={next =>
              setFiltroData([next.preset, next.start, next.end])
            }
          />
        }
      />

      <Flex minH="0" w="100%" my="3" mt="4" gridGap={2} flexWrap="wrap">
        <OktonSegmentedFilter<typeof filterStatus>
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'reconciled', label: 'Conciliados' },
          ]}
        />
        <OktonSearchFilter
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por descrição, conta, referência..."
        />
        <Flex ml="auto" borderWidth="1px" p="2" rounded="2xl" bg={bg}>
          <HStack spacing={2}>
            <Button
              leftIcon={<Plus size={18} />}
              colorScheme="blue"
              w="85%"
              onClick={() =>
                history.push('/extrato-bancario/novo', {
                  conta: filterAccount,
                })
              }
            >
              Novo Lançamento
            </Button>
            <Box minW="220px" w="100%">
              <OktonSearchSelect
                name="filtroconta"
                placeholder="Todas as contas"
                value={filterAccount}
                onChange={valor => setFilterAccount(valor)}
                options={accounts.map(a => ({
                  value: a.id_cts,
                  label: a.descricao_cts,
                }))}
              />
            </Box>
          </HStack>
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={recarregar} />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={BookOpen}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhum lançamento encontrado'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  data_exb: tx => (
                    <Box color="gray.600" whiteSpace="nowrap">
                      {formatDataExtrato(tx.data_exb)}
                    </Box>
                  ),
                  descricao_exb: tx => (
                    <Box
                      color={color}
                      fontWeight="bold"
                      maxW="220px"
                      isTruncated
                    >
                      {tx.descricao_exb}
                    </Box>
                  ),
                  descricao_cts: tx => (
                    <Box color="gray.500">{tx.descricao_cts ?? '—'}</Box>
                  ),
                  referencia_exb: tx => (
                    <Box color="gray.500">{tx.referencia_exb ?? '—'}</Box>
                  ),
                  /* O FITID é o identificador que o próprio banco deu ao
                  lançamento: não é a chave de deduplicação daqui, mas é por
                  ele que se acha a linha no extrato do banco. Vem longo e em
                  tamanho variável, então fica cortado com o valor inteiro no
                  title. */
                  fitid_exb: tx => (
                    <Box
                      color="gray.400"
                      fontSize="xs"
                      fontFamily="mono"
                      maxW="140px"
                      isTruncated
                      title={tx.fitid_exb ?? ''}
                    >
                      {tx.fitid_exb ?? '—'}
                    </Box>
                  ),
                  valor_exb: tx => (
                    <Box
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      color={
                        Number(tx.valor_exb) >= 0 ? 'green.600' : 'red.500'
                      }
                    >
                      {formatValue(Number(tx.valor_exb))}
                    </Box>
                  ),
                  conciliado_exb: tx => (
                    <>
                      {tx.conciliado_exb === '1' ? (
                        <Badge colorScheme="green" borderRadius="full" px="2">
                          Conciliado
                        </Badge>
                      ) : (
                        <Badge colorScheme="orange" borderRadius="full" px="2">
                          Pendente
                        </Badge>
                      )}
                    </>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: tx => (
                    <Flex justify="flex-end" gridGap="1">
                      {tx.conciliado_exb !== '1' && (
                        <IconButton
                          aria-label="Remover"
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          icon={<Trash2 size={16} />}
                          onClick={() => setItemExcluir(tx.id_exb)}
                        />
                      )}
                    </Flex>
                  ),
                },
              ]}
              getRowId={tx => tx.id_exb}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remove}
        title="Confirmar exclusão"
        description="Remover este lançamento do extrato?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default ExtratoBancario;
